import { useState, useRef, useEffect } from 'react';
import api from '../utils/api';
import Badge from './Badge';

export default function HexawarePOImportModal({ onClose, onComplete }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const inputRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [contractors, setContractors] = useState([]);
  useEffect(() => {
    api.get('/vendors').then(r => setVendors(r.data));
    api.get('/clients').then(r => {
      setClients(r.data);
      const hexaware = r.data.find(c => c.name && c.name.toLowerCase().includes('hexaware'));
      if (hexaware) setSelectedClientId(hexaware.id);
    });
    api.get('/contractors').then(r => setContractors(r.data));
  }, []);

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setError('');
    setPreview(null);
  }

  async function runPreview() {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/purchase-orders/import/hexaware/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      setRows(r.data.matched || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read this file — expected a Hexaware PO export');
    } finally {
      setLoading(false);
    }
  }

  function updateRow(idx, field, value) {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  async function overrideContractor(idx, contractorId) {
    const row = rows[idx];
    if (!contractorId) {
      updateRow(idx, 'contractor_id', null);
      return;
    }
    try {
      const r = await api.post('/purchase-orders/import/hexaware/resolve-contractor', {
        contractor_id: Number(contractorId),
        client_wo_number: row.client_wo_number,
      });
      setRows(rs => rs.map((row2, i) => i === idx ? {
        ...row2,
        contractor_id: r.data.contractor_id,
        contractor_matched_name: r.data.contractor_matched_name,
        wo_id: r.data.wo_id,
        wo_action: r.data.wo_action,
        vendor_id: r.data.vendor_id,
        rate: r.data.rate,
        buy_rate: r.data.buy_rate,
        match_confidence: 'manual',
      } : row2));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not resolve contractor');
    }
  }

  async function runCommit() {
    setCommitting(true);
    try {
    const rowsWithClient = rows.map(row => ({ ...row, client_id: selectedClientId }));
      const r = await api.post('/purchase-orders/import/hexaware/confirm', { pos: rowsWithClient });
      setResult(r.data);
      onComplete?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed');
    } finally {
      setCommitting(false);
    }
  }

  function rowContractorSubtype(r) {
    const c = contractors.find(c => c.id === r.contractor_id);
    return c?.contractor_subtype || null;
  }
  function rowNeedsVendor(r) {
    return rowContractorSubtype(r) === 'corp_to_corp';
  }
  const needsVendor = rows.filter(r => r.wo_action === 'create_new' && rowNeedsVendor(r) && !r.vendor_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="import-modal" style={{ width: 920 }} onClick={e => e.stopPropagation()}>
        <div className="drawer-header">
          <h3 className="drawer-title">Import Hexaware PO Export</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        {!preview && !result && (
          <>
            <div
              className={'dropzone' + (dragOver ? ' dragover' : '')}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => inputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              <input ref={inputRef} type="file" accept=".xls,.xlsx,.html" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} />
              {file ? (
                <div><strong>{file.name}</strong> selected</div>
              ) : (
                <div>
                  Drag the Hexaware PO export file here, or click to browse.<br />
                  <span className="form-hint">Contractor names are matched automatically (exact, then fuzzy). Review matches below before confirming.</span>
                </div>
              )}
            </div>
            {error && <div style={{ margin: '0 24px 16px', color: 'var(--danger-text)', fontSize: 13 }}>{error}</div>}
            <div className="drawer-footer">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={runPreview} disabled={!file || loading}>
                {loading ? 'Reading file…' : 'Preview import'}
              </button>
            </div>
          </>
        )}

        {preview && !result && (
          <>
            <div className="import-summary">
              <div className="import-summary-stat">
                <div className="num">{rows.filter(r => r.wo_action === 'existing').length}</div>
                <div className="label">Matched to existing WO</div>
              </div>
              <div className="import-summary-stat">
                <div className="num">{rows.filter(r => r.wo_action === 'create_new').length}</div>
                <div className="label">Will create new WO</div>
              </div>
              <div className="import-summary-stat">
                <div className="num" style={{ color: preview.unmatched.length ? 'var(--danger-text)' : undefined }}>{preview.unmatched.length}</div>
                <div className="label">Unmatched contractor</div>
              </div>
              <div className="import-summary-stat">
                <div className="num">{preview.skipped.length}</div>
                <div className="label">Skipped</div>
              </div>
            </div>


            <div style={{ margin: '14px 24px', padding: 12, background: 'var(--input-bg)', borderRadius: 'var(--radius)', borderLeft: '2px solid var(--primary)' }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Client for this import</label>
              <select value={selectedClientId || ''} onChange={e => setSelectedClientId(Number(e.target.value))} style={{ width: '100%', maxWidth: 300 }}>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {!selectedClientId && <div style={{ fontSize: 11, color: 'var(--warning-text)', marginTop: 6 }}>Please select a client</div>}
            </div>
            {needsVendor.length > 0 && (
              <div style={{ margin: '14px 24px', padding: 12, background: 'var(--warning-bg)', color: 'var(--warning-text)', borderRadius: 'var(--radius)', fontSize: 13 }}>
                {needsVendor.length} row(s) need a vendor selected before they can create a new Work Order (Contract Placement requires one).
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
              <table className="data-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>PO #</th><th>Contractor</th><th>Match</th><th>CWK #</th><th>WO</th><th>Vendor</th><th>Rate</th><th>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.po_number}>
                      <td>{r.po_number}</td>
                      <td>
                        {r.match_confidence === 'exact' ? (
                          r.contractor_matched_name
                        ) : (
                          <div>
                            <div style={{ fontSize: 12, marginBottom: 4 }}>
                              {r.contractor_matched_name
                                ? r.contractor_matched_name
                                : <span style={{ color: 'var(--danger-text)' }}>{r.contractor_name} (no match)</span>}
                            </div>
                            <select value={r.contractor_id || ''} onChange={e => overrideContractor(idx, e.target.value)} style={{ minWidth: 160 }}>
                              <option value="">{r.contractor_id ? 'Change contractor…' : 'Select contractor…'}</option>
                              {[...contractors].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)).map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                            </select>
                          </div>
                        )}
                      </td>
                      <td>
                        <Badge variant={r.match_confidence === 'exact' ? 'success' : r.match_confidence === 'none' ? 'danger' : 'warning'}>
                          {r.match_confidence}
                        </Badge>
                      </td>
                      <td>{r.client_wo_number || '—'}</td>
                      <td>
                        {r.wo_action === 'existing' && <Badge variant="success">Existing</Badge>}
                        {r.wo_action === 'create_new' && <Badge variant="info">New WO</Badge>}
                        {r.wo_action === 'none' && <Badge variant="danger">No contractor</Badge>}
                      </td>
                      <td>
                        {r.wo_action === 'existing' ? (
                          r.vendor_id ? (vendors.find(v => v.id === r.vendor_id)?.name || '—') : '—'
                        ) : r.wo_action === 'create_new' && rowNeedsVendor(r) ? (
                          <select value={r.vendor_id || ''} onChange={e => updateRow(idx, 'vendor_id', Number(e.target.value))} style={{ minWidth: 140 }}>
                            <option value="">Select vendor…</option>
                            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                          </select>
                        ) : r.wo_action === 'create_new' && rowContractorSubtype(r) ? (
                          <span className="form-hint">{rowContractorSubtype(r).toUpperCase()} — no vendor</span>
                        ) : '—'}
                      </td>
                      <td>
                        {r.wo_action === 'create_new' ? (
                          <input type="number" style={{ width: 70 }} value={r.rate || ''} onChange={e => updateRow(idx, 'rate', Number(e.target.value))} />
                        ) : (r.rate ? `$${r.rate}/hr` : '—')}
                      </td>
                      <td>{r.hours ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="drawer-footer">
              <button className="btn btn-secondary" onClick={() => setPreview(null)}>Back</button>
              <button className="btn btn-primary" onClick={runCommit} disabled={committing || needsVendor.length > 0}>
                {committing ? 'Importing…' : `Confirm import (${rows.length} rows)`}
              </button>
            </div>
          </>
        )}

        {result && (
          <>
            <div style={{ padding: 24 }}>
              <p>Created <strong>{result.created.length}</strong> Purchase Orders.</p>
              {result.workOrdersCreated.length > 0 && (
                <p><strong>{result.workOrdersCreated.length}</strong> new Work Order(s) were auto-created.</p>
              )}
              {result.errors.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <strong>{result.errors.length} row(s) had errors:</strong>
                  <ul>{result.errors.map((e, i) => <li key={i} style={{ fontSize: 13 }}>{e.po}: {e.error}</li>)}</ul>
                </div>
              )}
            </div>
            <div className="drawer-footer">
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
